import type { OptionsDocument, QuoteDocument, QuoteOptions } from 'paintscout'
import { getAreaSubstrateDimension } from '../../../util/calculate/get-areaSubstrate-dimension'
import { roundDimensionValue } from '../../../util/calculate/round-dimension-value'
import { getInchSuffix } from '../../../util/calculate/get-inch-suffix'
import type { QuoteItemSection, RenderableItemSubstrate } from '../../../..'
import { formatCurrency } from '../../../../util/format-currency'
import { getObjectLabels } from '../../../../util/get-object-labels'
import { formatCoatCount, resolveProductCoats } from '../../../util/resolve-product-coats'

/**
 * @description Build label for renderableSubItem
 * @returns Label for item <string>
 */
export const getItemLabel = ({
  item,
  quoteOptions,
  options,
  section,
  totalPrice,
  quote,
  hideDimensions
}: {
  item: RenderableItemSubstrate
  options: OptionsDocument
  quoteOptions: QuoteOptions
  section: QuoteItemSection
  quote: QuoteDocument
  totalPrice: number
  hideDimensions?: boolean
}): string => {
  // Construct label elements to combine
  const labelElements = []

  if (item.rateType === 'quantity' && !item.hideQuantity) {
    labelElements.push(item.quantity)
  }

  const inlineAreaOptions = section === 'options' || section === 'pending' || section === 'additional'
  if (!quoteOptions.showDetailedBreakdown && !quoteOptions.stackSubstrates && item.showPrice && inlineAreaOptions) {
    labelElements.push(formatCurrency({ options, value: totalPrice }))
  }
  if (quoteOptions.stackSubstrates && item.showPrice) {
    labelElements.push(formatCurrency({ options, value: totalPrice }))
  }

  const { distinct: distinctProductCoats, isMixed: coatsAreMixed } = resolveProductCoats(
    item.coats,
    item.products ?? []
  )
  // When the products themselves are shown and coats are enabled, differing coat counts are
  // folded into each product's own text below instead of a single (ambiguous) number here.
  const foldCoatsIntoProducts =
    coatsAreMixed && item.showProduct && item.showCoats && item.useCoats && item.products?.length > 0

  if (item.showCoats && item.useCoats && !foldCoatsIntoProducts) {
    if (coatsAreMixed) {
      labelElements.push(`${distinctProductCoats.join(', ')} Coats`)
    } else {
      const coatsValue = distinctProductCoats.length === 1 ? distinctProductCoats[0] : item.coats
      labelElements.push(formatCoatCount(Number(coatsValue)))
    }
  }

  if (item.showPrep && item.prep) {
    labelElements.push(`${item.prep} hr${item.prep === 1 ? '' : 's'} prep`)
  }

  if (item.showDimensions && !hideDimensions) {
    const rateType = item.rateType
    const isLnft = rateType === 'lnft'
    const itemArea = Object.values(quote.areas).find((area) => area.key === item.areaKey)
    const isRoom = itemArea?.area_type?.value === 'room'
    const objectLabels = getObjectLabels({ options })
    const inchSuffix = getInchSuffix({ useInches: item.useInches, unitValue: objectLabels.unit.value })

    const hasCustomSubstrateDimensions =
      item.length.useCustom || item.width.useCustom || item.height.useCustom || item.dimensionOverride != null

    let dimensionValue: number
    if (hasCustomSubstrateDimensions) {
      const length = item?.length?.useCustom ? item?.length?.custom ?? 0 : item?.length?.default ?? 0
      const width = item?.width?.useCustom ? item?.width?.custom ?? 0 : item?.width?.default ?? 0
      const height = item?.height?.useCustom ? item?.height?.custom ?? 0 : item?.height?.default ?? 0
      dimensionValue =
        item.dimensionOverride != null
          ? item.dimensionOverride
          : getAreaSubstrateDimension({ length, width, height, isRoom, rateType, useInches: item.useInches ?? false })
    } else {
      // Fall back to area-level dimensions, same as getDimension in AreaSubstrates.tsx
      const areaDims = itemArea?.dimensions
      switch (rateType) {
        case 'lnft':
          dimensionValue = Number(areaDims?.lnft) || 0
          break
        case 'sqftWalls':
        case 'sqft_walls':
          dimensionValue = isRoom ? Number(areaDims?.sqft_walls) || 0 : Number(areaDims?.sqft) || 0
          break
        case 'sqftFloor':
        case 'sqft_floor':
          dimensionValue = isRoom ? Number(areaDims?.sqft_floor) || 0 : Number(areaDims?.sqft) || 0
          break
        case 'sqftCeiling':
        case 'sqft_ceiling':
          dimensionValue = isRoom ? Number(areaDims?.sqft_ceiling) || 0 : Number(areaDims?.sqft) || 0
          break
        default:
          dimensionValue = Number(areaDims?.sqft) || 0
          break
      }
    }

    const dimension = `${roundDimensionValue(dimensionValue)} ${
      isLnft ? objectLabels.unit.abbreviation.linear : objectLabels.unit.abbreviation.square
    }${inchSuffix}`
    labelElements.push(dimension)
  }

  // Include product details if present
  if (item.showProduct && item.products.length) {
    const { products } = item
    for (const product of products) {
      const productParts = [product.label, product.color]
      if (foldCoatsIntoProducts) {
        productParts.push(formatCoatCount(Number(product.coats ?? item.coats)))
      }
      const productString = productParts.filter((part) => part).join(' - ')
      if (productString) {
        labelElements.push(productString)
      }
    }
  }

  // Combine elements into full description
  const labelString = labelElements.length ? ` (${labelElements.join(', ')})` : ''

  return labelString
}
